import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Stack,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  Select,
  FormControl,
  MenuItem,
  FormHelperText,
  ListSubheader,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { HeadTag } from 'shared/components';
import {
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { AIFeedbackSchema } from 'validators/AIFeedbackSchema';
import { PublicService } from 'services/PublicService';
import { Utils } from 'shared/helpers';

export interface FeedbackType {
  type: string;
  example: string;
}
export interface FeedbackFormValues {
  _id: string;
  category: string;
  feedbackType: FeedbackType[];
  createdAt: string;
  updatedAt: string;
}

const FeedbackForm = (props: any): ReactElement => {
  const { aiSolutions } = props;
  const [feedbackTypes, setFeedbackTypes] = useState<FeedbackFormValues[]>([]);
  const intialValues = {
    aisystemId: '',
    name: '',
    email: '',
    category: '',
    feedback: '',
    isAgree: false,
    feedbackType: '',
  };
  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    const { isAgree, ...rest } = formValues;
    console.log(isAgree);
    PublicService.updateFeedback({ ...rest })
      .then((res) => {
        Utils.handleSuccessResponse(res);
        formik.resetForm();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik.setSubmitting(false);
      });
  };

  const getFeedbackTypes = (): void => {
    PublicService.getFeedbackTypes()
      .then((res) => {
        const { data } = res.data;
        setFeedbackTypes(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  useEffect(() => {
    getFeedbackTypes();
  }, []);

  return (
    <>
      <Box className="feedback-management add-feedback">
        <HeadTag title="AI Feedback Form" />

        <Paper elevation={0} className="feedback-form-container">
          <Formik
            initialValues={intialValues}
            validationSchema={AIFeedbackSchema}
            validateOnMount
            enableReinitialize
            validateOnBlur
            onSubmit={(formValues, formik) => {
              actionOnFormSubmit(formValues, formik);
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              values,
              setFieldValue,
              isValid,
              handleChange,
              touched,
              errors,
              handleBlur,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3} className="add-feedback-wrapper">
                  <Grid item xs={12}>
                    <Paper variant="outlined" className="p-4">
                      <Typography variant="h3" className="section-title">
                        Select AI Solution
                      </Typography>
                      <FormControl
                        fullWidth
                        error={touched.aisystemId && Boolean(errors.aisystemId)}
                      >
                        <Select
                          name="aisystemId"
                          value={values.aisystemId || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) return 'Select an AI Solution';

                            const selectedItem = aiSolutions?.find(
                              (item: any) =>
                                String(item.aisystemId) === selected
                            );

                            return selectedItem
                              ? selectedItem.solutionName
                              : '';
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select AI Solution
                          </MenuItem>

                          {aiSolutions?.map((item: any) => (
                            <MenuItem
                              key={item.aisystemId}
                              value={String(item.aisystemId)}
                            >
                              <Typography variant="body1">
                                {item.solutionName}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                  </Grid>

                  {/* User Info */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" className="p-4">
                      <Typography variant="h3" className="section-title">
                        Your Information
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <AppFormikTextField
                            name="name"
                            placeholder="Enter your full name"
                            label="Your Name *"
                            outerLabel
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <AppFormikTextField
                            name="email"
                            placeholder="Enter your email address"
                            label="Your Email *"
                            type="email"
                            outerLabel
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Feedback Details */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" className="p-4">
                      <Typography variant="h3" className="section-title">
                        Feedback Details
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          {feedbackTypes && (
                            <FormControl
                              fullWidth
                              error={
                                touched.feedbackType &&
                                Boolean(errors.feedbackType)
                              }
                            >
                              <Select
                                value={values.feedbackType || ''}
                                displayEmpty
                                onChange={(e) => {
                                  const selectedType = e.target.value;

                                  const matched = feedbackTypes
                                    ?.flatMap((cat: any) =>
                                      (cat.feedbackType || []).map(
                                        (f: any) => ({
                                          type: f.type,
                                          category: cat.category,
                                        })
                                      )
                                    )
                                    ?.find(
                                      (item: any) => item.type === selectedType
                                    );

                                  if (matched) {
                                    setFieldValue('feedbackType', matched.type);
                                    setFieldValue('category', matched.category);
                                  }
                                }}
                                renderValue={(selected) => {
                                  if (!selected) return 'Select Feedback Type';
                                  return selected;
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select Feedback Type
                                </MenuItem>

                                {feedbackTypes?.flatMap((categoryItem: any) => [
                                  <ListSubheader key={categoryItem._id}>
                                    <Typography
                                      variant="h6"
                                      fontWeight={600}
                                      sx={{ color: 'text.primary' }}
                                    >
                                      {categoryItem.category}
                                    </Typography>
                                  </ListSubheader>,

                                  ...(categoryItem?.feedbackType || []).map(
                                    (fbItem: any) => (
                                      <MenuItem
                                        key={`${categoryItem._id}-${fbItem.type}`}
                                        value={fbItem.type}
                                        sx={{ pl: 3 }}
                                      >
                                        <Typography
                                          variant="h6"
                                          className="mx-3"
                                        >
                                          {fbItem.type}
                                        </Typography>
                                      </MenuItem>
                                    )
                                  ),
                                ])}
                              </Select>

                              {touched.feedbackType && errors.feedbackType && (
                                <FormHelperText>
                                  {errors.feedbackType}
                                </FormHelperText>
                              )}
                            </FormControl>
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <AppFormikTextArea
                            name="feedback"
                            placeholder="Please provide detailed feedback..."
                            label="Feedback *"
                            rows={6}
                            outerLabel
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper variant="outlined" className="p-4">
                      <Grid item xs={12} style={{ marginBottom: '20px' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isAgree}
                              onChange={(e) =>
                                setFieldValue('isAgree', e.target.checked)
                              }
                              name="isAgree"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              className="consent-text"
                            >
                              I understand that this feedback will be reviewed
                              by Tempo Pty Ltd and may be used to improve their
                              AI solutions. I consent to the processing of this
                              data for the purpose of AI governance and
                              compliance.
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2}>
                          <AppPrimaryButton
                            sx={{ width: '100%' }}
                            isLoading={isSubmitting}
                            disabled={isSubmitting || !isValid}
                            title="Submit Feedback"
                            type="submit"
                          />
                        </Stack>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Paper>
      </Box>
    </>
  );
};

export default FeedbackForm;
